import { Field, Formik } from "formik";
import React, { useEffect } from "react";
import { Button, Col, Form, Input, Label, Modal, Row } from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { globalAPI } from "./global";
import { queryKeys } from "./queryKeys";
import { loadingToast, successToast, tog_backdrop } from "./common";
import horizontalLogo from "./Media/popupLogo.png";
import {
  FuelEUResult,
  balanceRatingClass,
  formatBalance,
  formatEnergy,
  formatEuro,
  formatNumber,
  formatPercent,
  gramsToTonnes,
} from "./fueleu";
import "./App.css";

interface FuelEUResultsModalProps {
  state: boolean;
  setState: any;
  id: number;
  result: FuelEUResult | null;
  vesselName: string;
  year: number;
}

const LEAD_FIELDS = [
  { name: "name", label: "Full name" },
  { name: "designation", label: "Designation" },
  { name: "company_name", label: "Company name" },
  { name: "email", label: "E-mail" },
  { name: "mobile", label: "Mobile" },
];

/**
 * The FuelEU results screen.
 *
 * Structurally the CII modal's twin — verdict on the left, lead capture on the
 * right, PATCHing the contact details onto the record the Calculate button just
 * created — but it shows the calculation rather than a teaser for one. FuelEU's
 * answer is a set of numbers an operator acts on (how many tonnes, what penalty,
 * at what blend), not a single letter grade, so there is nothing to withhold that
 * would still leave something worth reading.
 */
const FuelEUResultsModal = ({
  state,
  setState,
  id,
  result,
  vesselName,
  year,
}: FuelEUResultsModalProps) => {
  useEffect(() => {
    toast.dismiss();
  }, []);

  const status = result?.is_compliant ? "Surplus" : "Deficit";
  const optimisation = result?.optimisation;

  return (
    <Modal
      isOpen={state}
      toggle={() => tog_backdrop(setState)}
      backdrop="static"
      id="fueleuModal"
      size="xl"
      className="mt-5"
      /* The results run to a verdict, five figures and two tables, which is
         taller than a laptop viewport. `scrollable` scrolls the body inside the
         dialog so the lead form on the right stays put and reachable; without it
         the whole modal grows and the submit button ends up below the fold. */
      scrollable
    >
      <div className="modal-body img-bg">
        <Row className="mb-0">
          <Col>
            <button
              type="button"
              className="btn-close float-right"
              onClick={() => setState(false)}
              aria-label="Close"
            />
          </Col>
        </Row>

        <Row className="g-4">
          <Col lg={7} className="result-pane">
            <Row className="mb-4 align-items-center">
              <Col xs="auto">
                <img src={horizontalLogo} alt="" height="45" />
              </Col>
              <Col>
                <h3 className="modal-title">FuelEU Maritime Results</h3>
              </Col>
            </Row>

            {result ? (
              <>
                <h2 className="fueleu-verdict-heading">
                  {vesselName}&rsquo;s {year} compliance balance is a
                </h2>
                <div
                  className={`CII-rating-${balanceRatingClass(
                    status
                  )} fueleu-verdict`}
                >
                  <span className="fueleu-verdict__word">{status}</span>
                  <span className="fueleu-verdict__figure">
                    {formatBalance(result.compliance_balance)} tCO₂eq
                  </span>
                </div>

                <dl className="fueleu-figures fueleu-figures--blurred">
                  <Figure
                    label="Attained GHG intensity"
                    value={`${formatNumber(
                      result.attained_intensity,
                      3
                    )} gCO₂eq/MJ`}
                  />
                  <Figure
                    label={`${year} target`}
                    value={`${formatNumber(
                      result.target_intensity,
                      3
                    )} gCO₂eq/MJ`}
                    note={`${formatNumber(
                      result.reference_intensity,
                      2
                    )} less ${formatPercent(result.reduction, 1)}`}
                  />
                  <Figure
                    label="Margin to target"
                    value={`${formatNumber(
                      result.intensity_margin,
                      3
                    )} gCO₂eq/MJ`}
                    /* margin = target − attained, so a positive margin is the
                       good direction: the mix came in *under* the limit. The
                       sign alone does not carry that - it reads as "less" to
                       anyone who has not just derived which way round the
                       subtraction goes. */
                    note={
                      result.intensity_margin >= 0
                        ? "Over achieved"
                        : "Under achieved"
                    }
                  />
                  <Figure
                    label="Total energy"
                    value={formatEnergy(result.total_energy)}
                  />
                  <Figure
                    label="Annex IV penalty"
                    value={formatEuro(result.penalty_eur)}
                    note={
                      result.is_compliant
                        ? "No penalty on a surplus"
                        : "€2,400 per tonne VLSFO-equivalent"
                    }
                  />
                  {!result.is_compliant && (
                    <Figure
                      label="Borrowable (Art. 20)"
                      /* An allowance, not a balance, so it takes no sign —
                         `formatBalance` would print it "+1.48", which reads as a
                         surplus inside a block that is reporting a deficit. */
                      value={`${formatNumber(
                        gramsToTonnes(result.borrowing_limit)
                      )} tCO₂eq`}
                      note="Up to 2% of the deficit, against next period"
                    />
                  )}
                </dl>

                {/* ------------------------------------- the fuel breakdown */}
                <h5 className="fueleu-section">Fuel mix</h5>
                <div className="fueleu-table-wrap">
                  <table className="fueleu-table">
                    <thead>
                      <tr>
                        <th>Fuel</th>
                        <th>Quantity</th>
                        <th>Intensity</th>
                        <th>Energy</th>
                        <th>Energy share</th>
                        <th>Emissions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.fuels.map((fuel, index) => (
                        <tr key={index}>
                          <td>{fuel.name ?? "—"}</td>
                          <td>
                            {formatNumber(fuel.mass, 2)} mt
                            {fuel.reward_multiplier > 1 && (
                              <em className="fueleu-tag">
                                RFNBO ×{fuel.reward_multiplier}
                              </em>
                            )}
                          </td>
                          <td>{formatNumber(fuel.intensity, 3)}</td>
                          <td>{formatEnergy(fuel.energy)}</td>
                          <td>{formatPercent(fuel.energy_share)}</td>
                          <td>
                            {formatNumber(fuel.emissions / 1e6, 2)} tCO₂eq
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* ----------------------------------------- the optimiser */}
                <h5 className="fueleu-section">Reaching a zero balance</h5>
                {optimisation?.feasible ? (
                  <>
                    <p className="fueleu-optimiser-lead">
                      Replacing{" "}
                      <strong>
                        {formatNumber(optimisation.displaced_fossil_mass, 2)} mt
                      </strong>
                      {optimisation.displaced_share_of_its_own_fuel != null && (
                        <span className="fueleu-optimiser-note">
                          {" "}
                          (
                          {formatPercent(
                            optimisation.displaced_share_of_its_own_fuel,
                            1
                          )}
                          )
                        </span>
                      )}{" "}
                      of{" "}
                      <strong>
                        {optimisation.displaced_fuel ??
                          "your dirtiest fossil fuel"}
                      </strong>{" "}
                      with{" "}
                      <strong>
                        {formatNumber(optimisation.alt_mass, 2)} mt
                      </strong>
                      {optimisation.alt_share_of_total_mass != null && (
                        <span className="fueleu-optimiser-note">
                          {" "}
                          (
                          {formatPercent(
                            optimisation.alt_share_of_total_mass,
                            1
                          )}
                          )
                        </span>
                      )}{" "}
                      of{" "}
                      <strong>
                        {optimisation.alternative_fuel ?? "the alternative"}
                      </strong>{" "}
                      brings the balance to zero.
                    </p>
                    <dl className="fueleu-figures fueleu-figures--tight">
                      <Figure
                        label="Alternative fuel required"
                        value={`${formatNumber(optimisation.alt_mass, 3)} mt`}
                        note={optimisation.alternative_fuel ?? undefined}
                      />
                      <Figure
                        label="Fossil fuel displaced"
                        value={`${formatNumber(
                          optimisation.displaced_fossil_mass,
                          3
                        )} mt`}
                        note={optimisation.displaced_fuel ?? undefined}
                      />
                      <Figure
                        label="Share of total energy"
                        value={formatPercent(optimisation.energy_share, 3)}
                      />
                      <Figure
                        label="Resulting intensity"
                        value={`${formatNumber(
                          optimisation.projected_intensity,
                          3
                        )} gCO₂eq/MJ`}
                      />
                    </dl>
                  </>
                ) : (
                  <p className="fueleu-optimiser-lead fueleu-optimiser-lead--none">
                    {optimisation?.reason ??
                      "Select an alternative fuel to see what it would take."}
                  </p>
                )}

                {/* --------------------------------------- the sensitivity */}
                {result.sensitivity.length > 0 && (
                  <>
                    <h5 className="fueleu-section">Blend sensitivity</h5>
                    <div className="fueleu-table-wrap">
                      <table className="fueleu-table">
                        <thead>
                          <tr>
                            <th>Alternative share</th>
                            <th>
                              {optimisation?.alternative_fuel ?? "Alternative"}
                            </th>
                            <th>GHG intensity</th>
                            <th>Compliance balance</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.sensitivity.map((row, index) => (
                            <tr
                              key={index}
                              className={
                                row.status === "Break-even"
                                  ? "fueleu-table__breakeven"
                                  : undefined
                              }
                            >
                              <td>
                                <Blurred>
                                  {formatPercent(row.energy_share, 3)}
                                </Blurred>
                              </td>
                              <td>
                                <Blurred>
                                  {row.alt_mass === null
                                    ? "—"
                                    : `${formatNumber(row.alt_mass, 2)} mt`}
                                </Blurred>
                              </td>
                              <td>
                                <Blurred>
                                  {formatNumber(row.intensity, 3)}
                                </Blurred>
                              </td>
                              <td>
                                <Blurred>
                                  {formatBalance(row.compliance_balance)} tCO₂eq
                                </Blurred>
                              </td>
                              <td>
                                <Blurred>
                                  <span
                                    className={`fueleu-pill fueleu-pill--${balanceRatingClass(
                                      row.status
                                    )}`}
                                  >
                                    {row.status}
                                  </span>
                                </Blurred>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                <p className="fueleu-footnote">
                  Indicative, not a verifier-approved FuelEU report. Emission
                  factors are the published Annex I/II defaults; where a fuel has
                  no default, the certified pathway value you entered was used.
                  Ice-class adjustment, wind-assistance reward, pooling and
                  banking are not modelled.
                </p>
              </>
            ) : (
              <p>No result to show.</p>
            )}
          </Col>

          <Col lg={5} className="lead-pane">
            <Formik
              initialValues={{
                name: "",
                company_name: "",
                email: "",
                mobile: null,
                i_agree: false,
                designation: "",
              }}
              validationSchema={Yup.object({
                name: Yup.string().required("Field is required!"),
                company_name: Yup.string().required("Field is required!"),
                email: Yup.string()
                  .email("Invalid e-mail")
                  .required("Field is required!"),
                mobile: Yup.string()
                  .matches(/^\d{10}$/, "Please enter 10 digits")
                  .required("Field is required!"),
              })}
              onSubmit={(values: any, actions: any) => {
                actions.setSubmitting(false);
                loadingToast(
                  "Please wait as we prepare to send you this report via email"
                );
                globalAPI
                  .patch(`${queryKeys.FuelEUCalculator.url()}${id}/`, values)
                  .then((res) => {
                    if (res.status === 200) {
                      successToast(
                        "You will receive the report via email within a few minutes."
                      );
                      setState(false);
                    }
                  })
                  .catch((err) => {
                    console.log(err);
                  });
              }}
            >
              {(props) => (
                <Form onSubmit={props.handleSubmit} className="lead-form">
                  <h4 className="font-white text-center p-3">
                    Get our FREE FuelEU Report for your vessel directly to your
                    phone/email
                  </h4>
                  <p
                    className="mb-3 font-white text-center"
                    style={{ fontSize: "12px" }}
                  >
                    Please note that your email will not be spammed, and your
                    personal or professional information will not be shared with
                    third parties.
                  </p>
                  {LEAD_FIELDS.map(({ name, label }) => (
                    <div className="mb-2" key={name}>
                      <div className="form-floating">
                        <Field
                          type="text"
                          name={name}
                          id={name}
                          placeholder={label}
                          className="form-control"
                        />
                        <Label
                          htmlFor={name}
                          className="d-flex align-items-center asteric"
                        >
                          {label}
                        </Label>
                      </div>
                      {(props.errors as any)[name] && (
                        <Label className="error">
                          {(props.errors as any)[name]}
                        </Label>
                      )}
                    </div>
                  ))}
                  <div className="ele_row mt-3 mb-4">
                    <Input
                      type="checkbox"
                      name="i_agree"
                      id="i_agree"
                      onChange={props.handleChange}
                    />
                    <Label
                      htmlFor="i_agree"
                      className="mb-0 ms-2 font-white"
                    >
                      I would like to be contacted through SMS or WhatsApp from
                      Ecosail about my ship&rsquo;s FuelEU compliance
                    </Label>
                  </div>
                  <Button
                    type="submit"
                    style={{ backgroundColor: "#20a8a4" }}
                    className="btn logo-blue font-white w-100"
                  >
                    Get my FuelEU Report
                  </Button>
                </Form>
              )}
            </Formik>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

/**
 * Frosts over a value in the blend sensitivity ladder.
 *
 * That table is the part worth paying for — the whole curve of blends and where
 * it crosses zero — so it is shown as a shape without its numbers, and the lead
 * form on the right is what unfrosts it. Only the ladder uses this: the verdict,
 * the headline figures and the fuel mix stay readable. Cosmetic, not a lock —
 * the API sends these rows either way and they sit in the DOM.
 */
const Blurred = ({ children }: { children: React.ReactNode }) => (
  <span className="fueleu-blurred">{children}</span>
);

const Figure = ({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) => (
  <div className="fueleu-figure">
    <dt>{label}</dt>
    <dd>
      {value}
      {note && <em>{note}</em>}
    </dd>
  </div>
);

export default FuelEUResultsModal;
